import { Button, Form, Input } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLoading } from "../../context/LoadingContext";
import { useSendRegisterOtp } from "../../hooks/Authentication/auth.hooks";
import {
  EmailFormat,
  InputRequired,
  PasswordFormat,
} from "../../validations/validations";
import CustomPassword from "../CustomPassword";
import { P3 } from "../NormalFonts";
import { RegisterFormType } from "./data";
import styles from "./styles.module.scss";
const RegisterForm: React.FC<RegisterFormType> = ({ onFinish, loading }) => {
  const COOLDOWN_SEC = 60;
  let cooldownNumber: number | undefined;
  let cooldownInterval: NodeJS.Timeout;

  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { setIsLoading } = useLoading();
  const navigate = useNavigate();
  const { setLoginForm } = useAuth();
  const [cooldown, setCooldown] = useState<number | undefined>(undefined);
  const [otpAlreadySent, setOTPAlreadySent] = useState<boolean>(false);

  const handleOpenLoginPage = () => {
    setLoginForm();
  };

  const [loadingSendOTP, setLoadingSendOTP] = useState<boolean>(false);

  const { mutate: sendOTP } = useSendRegisterOtp();

  const initCoolDown = () => {
    setCooldown(COOLDOWN_SEC);
    cooldownNumber = COOLDOWN_SEC;
    cooldownInterval = setInterval(() => {
      setCooldown((cooldownNumber || 1) - 1);
      cooldownNumber = (cooldownNumber || 1) - 1;
      if (cooldownNumber <= 0) {
        setCooldown(undefined);
        cooldownNumber = undefined;
        clearInterval(cooldownInterval);
      }
    }, 1000);
  };

  const handleSendOTP = async () => {
    const value = form.getFieldsValue();
    if (!value.account) {
      form.validateFields(["account"]);
      return;
    }
    setLoadingSendOTP(true);
    await sendOTP(
      { params: { account: value.account } },
      {
        onSuccess: (success: any) => {
          setLoadingSendOTP(false);
          initCoolDown();
          setOTPAlreadySent(true);
          console.debug("success", success);
        },
        onError: (error: any) => {
          setLoadingSendOTP(false);
          form.setFields([
            {
              name: "account",
              errors: [
                t([
                  `error.${error.data.result.code}`,
                  error.data.result.message,
                ]),
              ],
            },
          ]);
          // CustomMessage({
          //   content: t([
          //     `error.${error.data.result.code}`,
          //     error.data.result.message,
          //   ]),
          // });
          console.debug("error", error);
        },
      }
    );
  };

  const renderSendOTPButtonText = () => {
    if (cooldown) return t("auth.button.otpsent", { second: cooldown });
    if (otpAlreadySent) return t("auth.button.resendotp");
    else return t("auth.button.sendotp");
  };

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  return (
    // <div className={styles.container}>
    <Form
      className={styles.container}
      autoComplete={"off"}
      onFinish={onFinish}
      form={form}
      requiredMark={false}
      layout={"vertical"}
    >
      <Form.Item
        name={"account"}
        label={t("auth.input.email")}
        rules={[InputRequired(t, "auth.input.email"), EmailFormat(t)]}
      >
        <Input placeholder={t("auth.hint.email")} />
      </Form.Item>
      <div className={styles.otpInputWrapper}>
        <Form.Item
          name={"otp"}
          className={styles.otpInput}
          rules={[InputRequired(t, "auth.otp")]}
        >
          <Input placeholder={t("auth.hint.otp")} />
        </Form.Item>
        <Button
          className={styles.requestOtpButton}
          type="link"
          size="small"
          loading={loadingSendOTP}
          onClick={handleSendOTP}
          disabled={cooldown !== undefined}
        >
          {renderSendOTPButtonText()}
        </Button>
      </div>
      <Form.Item
        name={"password"}
        label={t("auth.input.password")}
        rules={[
          InputRequired(t, "auth.input.password"),
          PasswordFormat(t, 6, 20),
        ]}
      >
        <CustomPassword placeholder={t("auth.hint.newpassword")} />
      </Form.Item>
      <Form.Item
        name={"confirm_password"}
        label={t("auth.input.repeatpassword")}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }

              return Promise.reject(new Error(t("error.repeat.pass")));
            },
          }),
        ]}
      >
        <CustomPassword placeholder={t("auth.hint.repeatpassword")} />
      </Form.Item>
      <Button type={"primary"} htmlType={"submit"} block>
        {t("normal.register")}
      </Button>
      <div className={styles.footer}>
        <Button type="link" size="small" onClick={handleOpenLoginPage}>
          {t("normal.login")}
        </Button>
        <P3 className={styles.desc} color="var(--login-text-label">
          {t("auth.register.confirm")}
        </P3>
        <div className={styles.footerButtonWrapper}>
          <Button
            style={{ color: "var(--login-text-link)" }}
            type="link"
            size="small"
          >
            {t("auth.terms")}
          </Button>
          <Button
            style={{ color: "var(--login-text-link)" }}
            type="link"
            size="small"
          >
            {t("auth.privacy")}
          </Button>
        </div>
      </div>
    </Form>
    // </div>
  );
};

export default RegisterForm;
